import React, { useState, useEffect, useCallback } from "react";
import { Form } from "react-bootstrap";
import { History, RefreshCw, Loader2 } from "lucide-react";
import {
  AppButton, AppCard, AppCardHeader, AppCardTitle, AppCardContent, AppBadge, EmptyState,
} from "../ui";
import apiClient from "../../utils/apiClient";

const ACTIONS = [
  { value: "", label: "All actions" },
  { value: "config.update", label: "Config updated" },
  { value: "conversation.pause", label: "Conversation paused" },
  { value: "conversation.resume", label: "Conversation resumed" },
  { value: "conversation.override.set", label: "Override changed" },
  { value: "draft.send", label: "Draft sent" },
  { value: "draft.edit", label: "Draft edited & sent" },
  { value: "draft.discard", label: "Draft discarded" },
  { value: "knowledge.create", label: "KB chunk created" },
  { value: "knowledge.update", label: "KB chunk updated" },
  { value: "knowledge.delete", label: "KB chunk deleted" },
  { value: "knowledge.bulk_update", label: "KB bulk update" },
  { value: "knowledge.seed", label: "KB seeded" },
  { value: "knowledge.reindex", label: "KB re-embedded" },
];

const ACTION_VARIANTS = {
  "config.update": "info",
  "conversation.pause": "warning",
  "conversation.resume": "success",
  "conversation.override.set": "info",
  "draft.send": "success",
  "draft.edit": "success",
  "draft.discard": "secondary",
  "knowledge.create": "success",
  "knowledge.update": "info",
  "knowledge.delete": "danger",
  "knowledge.bulk_update": "info",
  "knowledge.seed": "info",
  "knowledge.reindex": "secondary",
};

const fmt = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleString();
};

const AIAuditLogCard = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState("");
  const [page, setPage] = useState(0);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (action) params.set("action", action);
      params.set("limit", String(limit));
      params.set("skip", String(page * limit));
      const { data } = await apiClient.get(`/ai/whatsapp/audit?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (_e) {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [action, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <AppCard style={{ marginBottom: 20 }}>
      <AppCardHeader>
        <AppCardTitle><History size={16} /> AI action audit log</AppCardTitle>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Form.Select size="sm" style={{ width: 220 }} value={action} onChange={(e) => { setPage(0); setAction(e.target.value); }}>
            {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </Form.Select>
          <AppButton size="sm" variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 size={12} className="spin" /> : <RefreshCw size={12} />}
          </AppButton>
        </div>
      </AppCardHeader>
      <AppCardContent>
        {!loading && items.length === 0 && (
          <EmptyState title="No audit entries" description="Admin actions on AI config, drafts, conversations and the knowledge base appear here." />
        )}
        {items.length > 0 && (
          <div style={{ maxHeight: 480, overflowY: "auto" }}>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
                  <th style={{ padding: "6px 4px" }}>When</th>
                  <th style={{ padding: "6px 4px" }}>Action</th>
                  <th style={{ padding: "6px 4px" }}>Actor</th>
                  <th style={{ padding: "6px 4px" }}>Summary</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it._id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                    <td style={{ padding: "6px 4px", whiteSpace: "nowrap", color: "var(--text-secondary, #666)" }}>{fmt(it.createdAt)}</td>
                    <td style={{ padding: "6px 4px" }}>
                      <AppBadge variant={ACTION_VARIANTS[it.action] || "secondary"}>{it.action}</AppBadge>
                    </td>
                    <td style={{ padding: "6px 4px" }}>{it.actorName || it.actorRole || "—"}</td>
                    <td style={{ padding: "6px 4px" }}>{it.summary || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > limit && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
            <span style={{ fontSize: 12, color: "var(--text-secondary, #666)" }}>
              Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <AppButton size="sm" variant="outline" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0 || loading}>Prev</AppButton>
              <AppButton size="sm" variant="outline" onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * limit >= total || loading}>Next</AppButton>
            </div>
          </div>
        )}
      </AppCardContent>
    </AppCard>
  );
};

export default AIAuditLogCard;
